'use client'

import { InformationCircleIcon } from '@heroicons/react/20/solid'
import React from 'react'
import { BackgroundCloud, BackgroundGrid, BlogCTA } from '~/components/blog'
import { Button } from '~/components/ui/button'
import { cn, shuffle } from '~/lib/utils'

const demoParagraphs = [
  '지금 이 순간에도 수많은 사람들이 구글 혹은 네이버를 검색하며 정보를 찾고 있습니다. 맛집 추천이든, 새로운 제품의 리뷰든, 혹은 새로운 주제에 대한 정보든, 검색 엔진은 우리가 필요로 하는 정보를 제공하는 무한한 통로입니다. 그리고 이러한 정보의 대부분은 어디서 올까요? 네, 바로 블로그에서 나옵니다.',
  '블로그는 오늘날 우리가 찾는 대부분의 정보의 원천입니다. 어떤 레스토랑의 리뷰부터 최신 기술 제품에 대한 설명까지, 블로그는 다양한 주제에서 신뢰할 수 있는 정보를 제공합니다. 그런데 이렇게 중요한 블로그를 직접 작성하고 싶으신가요? 글쓰기가 어려운 분들에게 좋은 소식이 있습니다.',
  '누구나 정보를 얻기 위해 블로그를 읽습니다. 새로운 취미를 찾거나, 여행지를 조사하거나, 건강 팁을 얻기 위해 블로그는 언제나 도움이 됩니다. 하지만 직접 블로그를 작성하는 것은 어려울 수 있습니다. 그렇다면 이 문제를 어떻게 해결할 수 있을까요?',
  '당신은 오늘 어떤 정보를 검색했나요? 아마도 그 검색 결과 중에는 블로그가 꽤 있었을 것입니다. 블로그는 생생한 경험과 유용한 정보를 제공하는 독특한 콘텐츠입니다. 하지만 자신만의 블로그를 시작하려는 데 어려움을 느끼신다면, 이 글이 당신에게 도움이 될 것입니다.',
  '블로그는 생활의 다양한 분야에서 우리에게 가르침을 주는 중요한 정보원입니다. 식당의 리뷰, 여행 팁, 자동차 수리 방법 등, 블로그는 우리 생활의 모든 측면에 걸쳐 귀중한 지식을 제공합니다. 그렇다면 이렇게 유용한 블로그를 직접 작성하고 싶으나 어떻게 시작해야 할지 모르겠다면, 지금 읽고 계신 이 글이 당신의 해답이 될 수 있습니다.',
  '매일 수많은 사람들이 블로그를 통해 새로운 정보와 지식을 얻습니다. 최고의 커피를 내리는 방법, 효과적인 운동 루틴, 가족과 함께 갈 수 있는 휴가지 추천 등, 블로그는 우리의 일상에 풍요를 더해줍니다. 그런데, 당신 자신의 블로그를 시작하고 싶으나 글쓰기가 어렵다고 느낀다면, 이 글을 통해 그 문제를 해결할 수 있을 것입니다.',
  '블로그는 인터넷 세상의 보물입니다. 요리법부터 테크놀로지 리뷰, 건강한 생활습관까지, 블로그는 우리에게 다양한 주제의 실질적인 가이드를 제공합니다. 그렇다면, 이 보물 같은 정보의 일부가 되고 싶으신가요? 글쓰기에 어려움을 느끼신다면, 이 글이 그 첫걸음이 될 수 있습니다.',
  '오늘도 블로그가 당신의 질문에 대답했나요? 블로그는 우리가 궁금해하는 모든 주제에 대한 답을 제공하는 중요한 자원입니다. 하지만 블로그 작성은 쉽지 않을 수 있습니다. 글쓰기가 어렵고, 어떻게 시작해야 할지 모른다면, 이 글이 바로 당신의 지침서가 될 것입니다.',
  '블로그는 우리 일상생활의 빛나는 별입니다. 어떤 음식을 만들어 볼까, 어떤 책을 읽을까, 어디로 여행을 떠날까, 이 모든 질문에 블로그는 답을 줍니다. 그런데 이런 블로그를 직접 작성하고 싶으나 어디서부터 시작해야 할지 모르겠다면, 지금 이 글이 당신에게 도움이 될 것입니다.',
  '블로그를 통해 우리는 새로운 세상을 발견합니다. 특별한 요리법, 효과적인 운동 방법, 현명한 재테크 팁 등, 블로그는 우리에게 새로운 가능성을 열어줍니다. 그러나 이런 블로그를 스스로 작성하려는 데 어려움을 느낀다면, 이 글을 통해 그 벽을 넘어서 보세요.',
  '블로그는 정보의 바다입니다. 무엇이든지 알고 싶을 때, 블로그를 통해 수많은 지식과 경험을 얻을 수 있습니다. 당신도 이 정보의 바다에 뛰어들고 싶으나 글쓰기가 어렵다면, 지금 이 글을 통해 블로그 세계',
]

export default function StartBlog() {
  const [index, setIndex] = React.useState(0)
  const [loading, setLoading] = React.useState(false)
  const shffuledDemoParagraphs = React.useMemo(() => shuffle(demoParagraphs), [])
  const show = React.useCallback(() => setLoading(false), [])
  const onClick = React.useCallback(() => {
    if (loading || index > shffuledDemoParagraphs.length) return

    setLoading(true)
    setIndex((prevState) => prevState + 1)
    setTimeout(show, 1000)
  }, [loading, index, shffuledDemoParagraphs.length, show])

  return (
    <>
      <div className='relative isolate overflow-hidden py-24 sm:py-32'>
        <BackgroundCloud />
        <article className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto max-w-2xl lg:mx-0'>
            <p className='font-semibold text-indigo-600 text-lg leading-8 tracking-tight'>타입잇 블로그</p>
            <h1 className='mt-2 font-bold text-3xl text-gray-900 tracking-tight sm:text-4xl'>
              블로그를 시작하고 싶지만, 글쓰기가 어려운 당신에게
            </h1>
            <p className='mt-6 text-gray-700 text-xl leading-8'>
              블로그를 시작하는 것은 쉽지 않습니다. 블로그를 시작하고 싶지만 어떻게 시작해야 할지 모르겠다면, 이 글을
              읽어보세요.
            </p>
            <figure className='mt-16'>
              <img className='rounded-xl bg-gray-50 object-cover' src='/images/start-blog.jpg' alt='블로그 커버' />
              <figcaption className='mt-4 flex gap-x-2 text-gray-500 text-sm leading-6'>
                <InformationCircleIcon className='mt-0.5 h-5 w-5 flex-none text-gray-300' aria-hidden='true' />
                <a
                  href='https://unsplash.com/ko/%EC%82%AC%EC%A7%84/ylveRpZ8L1s?utm_source=unsplash&utm_medium=referral&utm_content=creditCopyText'
                  target='_blank'
                  rel='noreferrer noopener'
                >
                  Unsplash의
                </a>
                <a
                  href='https://unsplash.com/@sincerelymedia?utm_source=unsplash&utm_medium=referral&utm_content=creditCopyText'
                  target='_blank'
                  rel='noreferrer noopener'
                >
                  Sincerely Media
                </a>
              </figcaption>
            </figure>
          </div>
          <div className='mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-16 lg:mx-0 lg:mt-10 lg:max-w-none lg:grid-cols-12'>
            <div className='relative lg:order-last lg:col-span-5'>
              <BackgroundGrid />
              <figure className='border-indigo-600 border-l pl-8'>
                <blockquote className='font-semibold text-gray-900 text-xl leading-8 tracking-tight'>
                  <p>“타입잇으로 블로그를 쉽게 시작할 수 있었습니다.”</p>
                </blockquote>
                <figcaption className='mt-8 flex gap-x-4'>
                  <img
                    src='/images/jacob.png'
                    alt='제이콥 아바타'
                    className='mt-1 h-10 w-10 flex-none rounded-full bg-gray-50'
                  />
                  <div className='text-sm leading-6'>
                    <div className='font-semibold text-gray-900'>제이콥</div>
                    <div className='text-gray-600'>@jacob</div>
                  </div>
                </figcaption>
              </figure>
            </div>
            <div className='max-w-xl space-y-8 text-base text-gray-700 leading-7 lg:col-span-7'>
              <p>
                지금 이 순간에도 수많은 사람들이 구글 혹은 네이버를 검색하며 정보를 찾고 있습니다. 맛집 추천이든, 새로운
                제품의 리뷰든, 혹은 새로운 주제에 대한 정보든, 검색 엔진은 우리가 필요로 하는 정보를 제공하는 무한한
                통로입니다. 그리고 이러한 정보의 대부분은 어디서 올까요? 네, 바로 블로그에서 나옵니다.
              </p>
              <p>
                블로그는 다양한 주제에 대한 정보를 제공하는 독특한 공간입니다. 유명 블로거의 글부터, 블로그를 시작한지
                얼마 되지 않은 사람들의 솔직한 후기까지, 블로그는 정보를 제공하는 가장 인기 있는 플랫폼 중 하나입니다.
              </p>
              <p>
                유튜브, 인스타그램, 트위터 등의 등장으로 블로그의 인기가 줄어들었다고 생각할 수도 있습니다. 하지만,
                블로그는 여전히 흥미롭고 신뢰성 있는 정보를 얻는 주요한 곳입니다. 사람들은 대부분 광고보다는 실제
                사용자의 경험을 더 믿습니다. 그리고 블로그는 이러한 실제 경험을 공유하기에 가장 적합한 플랫폼입니다.
              </p>
              <p>
                이처럼 블로그는 여러 이유로 매우 중요한 플랫폼입니다. 하지만, 블로그 글을 작성하는 것은 많은 사람들에게
                큰 도전이 될 수 있습니다. 아무래도 글을 쓰는 것은 쉽지 않은 작업이니까요.
              </p>
              <h2 className='mt-16 font-bold text-2xl text-gray-900 tracking-tight'>
                이럴 때 필요한 것이 바로 &apos;타입잇&apos;입니다.
              </h2>
              <p>
                아래는 타입잇을 사용해 블로그를 작성하는 과정입니다. 한번 &quot;AI 카피 생성&quot; 버튼을 눌러보세요!
              </p>
              <div className='flex flex-col gap-4 rounded-lg border border-gray-200 p-4'>
                <div className='flex items-end justify-start gap-2'>
                  <img
                    className='h-8 w-8'
                    src='/images/writing_hand_color_light.svg'
                    alt='타입잇 로고'
                    width={32}
                    height={32}
                  />
                  <span className='font-bold'>블로그 머리글 작성</span>
                </div>
                <div className='isolate -space-y-px rounded-md shadow-xs'>
                  <div className='relative rounded-md rounded-b-none px-3 pt-2.5 pb-1.5 ring-1 ring-gray-300 ring-inset'>
                    <label htmlFor='name' className='block font-medium text-gray-900 text-xs'>
                      제목
                    </label>
                    <p className='block w-full border-0 p-0 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6'>
                      블로그를 시작하고 싶지만, 글쓰기가 어려운 당신에게
                    </p>
                  </div>
                  <div className='relative rounded-md rounded-t-none px-3 pt-2.5 pb-1.5 ring-1 ring-gray-300 ring-inset'>
                    <label htmlFor='job-title' className='block font-medium text-gray-900 text-xs'>
                      주제
                    </label>
                    <p className='block w-full border-0 p-0 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6'>
                      블로그를 작성하고 싶지만, 글쓰기가 어려운 분들에게 타입잇을 추천하는 글
                    </p>
                  </div>
                </div>
                <Button
                  type='button'
                  color='indigo'
                  onClick={onClick}
                  disabled={loading || index === shffuledDemoParagraphs.length - 1}
                >
                  <img
                    src='/images/magic_wand_color.svg'
                    alt='typeit logo small'
                    className='h-5 w-5'
                    width={20}
                    height={20}
                  />
                  <span className='mx-2 whitespace-nowrap'>AI 카피 생성</span>
                </Button>
                <ul className={cn(index >= 0 ? 'block' : 'hidden', 'reverse flex flex-col gap-2')}>
                  {index >= 0 &&
                    shffuledDemoParagraphs
                      .slice(0, index)
                      .reverse()
                      .map((paragraph, i) => (
                        <li
                          key={i}
                          className='flex flex-col gap-2 rounded-xl p-4 text-sm shadow-md ring-1 ring-gray-50'
                        >
                          {i === 0 && loading ? (
                            <>
                              <div className='h-4 w-full animate-pulse rounded-lg bg-gray-200' />
                              <div className='h-4 w-3/4 animate-pulse rounded-lg bg-gray-200' />
                              <div className='h-4 w-1/2 animate-pulse rounded-lg bg-gray-200' />
                            </>
                          ) : (
                            <p>{paragraph}</p>
                          )}
                        </li>
                      ))}
                </ul>
              </div>
              <p>어떠셨나요? 간단하지 않나요?</p>
              <p>
                타입잇은 블로그 작성을 위한 최적의 도구입니다. 단지 주제와 제목을 정하고, 타입잇에 입력만 하면 블로그
                글이 자동으로 작성됩니다.
              </p>
              <p>
                글쓰기가 어렵다고요? 타입잇이 그 어려움을 해결해 드립니다. 타입잇에는 다양한 템플릿이 준비되어 있어,
                간편하게 글을 작성할 수 있습니다. 또한, 웹사이트의 URL 주소만 입력하면 해당 웹사이트에 딱 맞는 디지털
                마케팅 컨텐츠도 제공합니다.
              </p>
              <p>
                이 글을 읽고 계신 당신도, 타입잇을 이용해 블로그를 시작해 보세요. 블로그 작성이 어렵다는 걱정을
                던져버리고, 타입잇과 함께 나만의 블로그 세상을 만들어보세요. 당신의 이야기가 시작될 준비가 되어
                있습니다.
              </p>
              <p className='border-gray-200 border-t pt-8 font-bold text-base'>
                이 블로그 게시물도 바로 타입잇을 이용하여 작성하였습니다. 여러분도 지금 바로 타입잇을 이용하여 블로그
                글쓰기를 시작해보세요!
              </p>
            </div>
          </div>
        </article>
        <BlogCTA />
      </div>
    </>
  )
}
